import * as THREE from 'three';
import { DRUID_PATH, NIGHTMARES, sendaForClass, type EnemyKind, type HeroClass, type SendaCine, type SendaFigure, type SendaWeather } from '@somnhold/content';
import {
  denLayout,
  DEN_RADIUS,
  pensLayout,
  PENS_RADIUS,
  hexLen,
  CHAMBER_RADIUS,
  chamberLayout,
  MOUTHS_RADIUS,
  mouthsLayout,
  GLADE_RADIUS,
  gladeLayout,
  KILN_RADIUS,
  kilnLayout,
  LEAF_RADIUS,
  leafLayout,
  CROWN_RADIUS,
  boughsLayout,
  HOLLOW_RADIUS,
  hollowLayout,
  SAP_RADIUS,
  sapLayout,
  PAST_RADIUS,
  pastLayout,
  ROOTS_RADIUS,
  underLayout,
  PINE_RADIUS,
  pineLayout,
  CABINET_RADIUS,
  cabinetLayout,
  cellId,
  createBattle,
  createClearingMap,
  createIncursionMap,
  hexToWorld,
  rimLayout,
  RIM_RADIUS,
  spanLayout,
  SPAN_RADIUS,
  stepBattle,
  WEDGE_RADIUS,
  wedgeLayout,
  WEIGHT_PIECES,
  weightLayout,
  weightsLayout,
  WEIGHTS_RADIUS,
  FORT_RADIUS,
  fortLayout,
  HALL_RADIUS,
  hallLayout,
  SLOPE_RADIUS,
  slopeLayout,
  AVALANCHE_RADIUS,
  avalancheLayout,
  CAVE_RADIUS,
  caveLayout,
  CRAG_RADIUS,
  cragLayout,
  PASS_RADIUS,
  passLayout,
  LOOSE_RADIUS,
  looseLayout,
  CANAL_RADIUS,
  canalFresh,
  canalLayout,
  canalRules,
  SALT_RADIUS,
  saltFresh,
  saltLayout,
  saltRules,
  sluiceFlow,
  ARCH_RADIUS,
  archFresh,
  archLayout,
  archRules,
  MON_RADIUS,
  monasteryFresh,
  monasteryLayout,
  monasteryRules,
  CIST_RADIUS,
  YARD_RADIUS,
  cisternLayout,
  yardLayout,
  seventhFresh,
  seventhRules,
  type BattleState,
  type Hero,
  type MapDef,
  BOX_RADIUS,
  STRUTS_RADIUS,
  KNEES_RADIUS,
  strutsLayout,
  boxLayout,
} from '@somnhold/sim';

/** The ledge the closing scene of the warrior's chapter 1 stands on (a layout whose answer has a gap by the scene's figures). */
const LEDGE_SCENE_SEED = 5;
/** The gorge the warrior's chapter 2 scenes stand on (its edge, its platform, its counterweight). */
const CHASM_SCENE_SEED = 11;

/** The fort the warrior's chapter 3 scenes stand on (its hearth in the west yard, its trail straight up at the top). */
const FORT_SCENE_SEED = 3;

/** A scene's board at the fort: the climb, the yards, the hall inside. */
const fortMap = (place: string): MapDef | null => {
  if (place === 'fortClimb') return { ...createClearingMap(SLOPE_RADIUS), rocks: slopeLayout(FORT_SCENE_SEED).rocks };
  if (place === 'fort' || place === 'fortPast' || place === 'fortFar') return { ...createClearingMap(FORT_RADIUS), rocks: fortLayout(FORT_SCENE_SEED).rocks };
  if (place === 'fortTomb' || place === 'fortTable' || place === 'fortHearth') return { ...createClearingMap(HALL_RADIUS), rocks: hallLayout().rocks };
  return null;
};

/** The avalanche's scenes (the warrior's chapter 4): the trail the figures stand on is this seed's. */
const SLIDE_SCENE_SEED = 3;

/** A scene's board on the avalanche's slopes: the loose slope's trail, the large wedges' slope, the ice cave. */
const slideMap = (place: string): MapDef | null => {
  if (place === 'loose' || place === 'slideFar') return { ...createClearingMap(LOOSE_RADIUS), rocks: looseLayout(SLIDE_SCENE_SEED).rocks };
  if (place === 'avalanche' || place === 'caveMouth') return { ...createClearingMap(AVALANCHE_RADIUS), rocks: avalancheLayout(SLIDE_SCENE_SEED).rocks };
  if (place === 'iceCave') return { ...createClearingMap(CAVE_RADIUS), rocks: caveLayout(SLIDE_SCENE_SEED).rocks };
  return null;
};

/** The Pass of the Rift's scenes (the warrior's chapter 5): the ledge and the pass of this seed (the fire east, slot 1 by it). */
const PASS_SCENE_SEED = 3;

/** A scene's board at the Pass of the Rift: the ledge of the wind, or the pass and its line. */
const passMap = (place: string): MapDef | null => {
  if (place === 'crag') return { ...createClearingMap(CRAG_RADIUS), rocks: cragLayout(PASS_SCENE_SEED).rocks };
  if (place === 'pass' || place === 'passFire' || place === 'passFar') return { ...createClearingMap(PASS_RADIUS), rocks: passLayout(PASS_SCENE_SEED).rocks };
  return null;
};

/** The paladin's chapter 1 scenes on the Burn's glass shore: the canals of this seed (the close: the test's, two pools). */
const SHORE_SCENE_SEED = 5;

/** A scene's board on the Way of the Altars: the glass shore's canals, a clearing at the watchtower or on the glass. */
const altarMap = (place: string, lit: boolean): MapDef | null => {
  if (place === 'glassShore') return { ...createClearingMap(CANAL_RADIUS), rocks: canalLayout(SHORE_SCENE_SEED, lit ? 'ebb' : 'small').rocks };
  if (place === 'watchtower' || place === 'glassNight' || place === 'temple' || place === 'keepYard') return createClearingMap(6);
  return null;
};

/** The druid's chapter 1 scenes: Olmo's clearing with its flood (the tree scene, the close), a clearing of six for the rest. */
const BOSQUE_SCENES = new Set(['groveLeaf', 'grove', 'groveYard', 'rootEave']);
const bosqueMap = (place: string, seed: number): MapDef | null =>
  place === 'olmoGlade' || place === 'olmoFace' ? { ...createClearingMap(GLADE_RADIUS), rocks: gladeLayout(seed, DRUID_PATH.glade.shoots).rocks } : BOSQUE_SCENES.has(place) ? createClearingMap(6) : null;

/** The druid's chapter 2 scenes: Hob's slope with its kilns (the cinematic, the opening), the old pine (Espino, the close), the campfire's rock. */
const hornosMap = (place: string, seed: number): MapDef | null =>
  place === 'kilnSlope' || place === 'kilnCine'
    ? { ...createClearingMap(KILN_RADIUS), rocks: kilnLayout(seed, DRUID_PATH.kilns.n).rocks }
    : place === 'oldPine' || place === 'pineFace'
      ? { ...createClearingMap(PINE_RADIUS), rocks: pineLayout(seed, DRUID_PATH.strip.trees).rocks }
      : place === 'nightRock'
        ? createClearingMap(6)
        : null;

/** The druid's chapter 3 scenes: the autumn wood (the cinematic, Ruda's story), the memory's slope, the maple's net (the close), Los Hornos and Ruda's yard. */
const otonoMap = (place: string, seed: number): MapDef | null =>
  place === 'autumnCine' || place === 'mapleFoot'
    ? { ...createClearingMap(LEAF_RADIUS), rocks: leafLayout(seed).rocks }
    : place === 'autumnPast' || place === 'mapleCarve'
      ? { ...createClearingMap(PAST_RADIUS), rocks: pastLayout(seed).rocks }
      : place === 'mapleFace'
        ? { ...createClearingMap(ROOTS_RADIUS), rocks: underLayout(seed, DRUID_PATH.under.pre).rocks }
        : place === 'hornosTown' || place === 'rudaYard'
          ? createClearingMap(6)
          : null;

/** The druid's chapter 4 scenes: the crown's boughs (the cinematic, the opening), the hollow (the bandage, the campfire), the sap's crowns, the top. */
const copaMap = (place: string, seed: number): MapDef | null =>
  place === 'crownCine' || place === 'crownBoughs'
    ? { ...createClearingMap(CROWN_RADIUS), rocks: boughsLayout(seed).rocks }
    : place === 'crownHollow' || place === 'crownNight'
      ? { ...createClearingMap(HOLLOW_RADIUS), rocks: hollowLayout(seed).rocks }
      : place === 'crownSap'
        ? { ...createClearingMap(SAP_RADIUS), rocks: sapLayout(seed, DRUID_PATH.sap.need).rocks }
        : place === 'crownTop'
          ? createClearingMap(8)
          : null;

/** The druid's chapter 5 scenes: the Grandsire's foot (standing or fallen), the hollow of the campfire, the gallery, the Carcoma's chamber. */
const raicesMap = (place: string, seed: number): MapDef | null =>
  place === 'grandsireFoot' || place === 'grandsireFallen'
    ? createClearingMap(8)
    : place === 'sapHollow'
      ? createClearingMap(6)
      : place === 'sapGallery'
        ? { ...createClearingMap(MOUTHS_RADIUS), rocks: mouthsLayout(seed).rocks }
        : place === 'carcomaMouth' || place === 'carcomaChamber'
          ? { ...createClearingMap(CHAMBER_RADIUS), rocks: chamberLayout(seed).rocks }
          : null;

/** The ranger's chapter 1 scenes (Aster's map, the Atalaya, the yard, the rim, the camp, the slope): a clearing of six for each. */
const ASTER_SCENES = new Set(['asterMap', 'asterTable', 'asterYard', 'furrowLip', 'furrowCamp', 'fallenBase', 'flankSea', 'asterLedge', 'cliffGrab', 'cliffFoot']);
const asterMap = (place: string): MapDef | null => (ASTER_SCENES.has(place) ? createClearingMap(6) : null);

/** The artificer's chapter 1 scenes (Nadd's workshop, the bastion at night): a clearing of six for each. */
const TALLER_SCENES = new Set(['bastionGate', 'forgeYard', 'shopTop', 'naddForge', 'tormoHall']);
const tallerMap = (place: string): MapDef | null => (TALLER_SCENES.has(place) ? createClearingMap(6) : null);

/** The mage's chapter 1 scenes: a clearing of six, the fire room after (its own room, from the scene's seed). */
const BIBLIO_SCENES = new Set(['stilts', 'pier', 'labYard', 'catalog', 'collapse']);
const biblioMap = (place: string, seed: number): MapDef | null =>
  place === 'fireOut' ? { ...createClearingMap(CABINET_RADIUS), rocks: cabinetLayout(seed).rocks } : BIBLIO_SCENES.has(place) ? createClearingMap(6) : null;

/** The artificer's chapter 2 scenes: the winch house on its own board (the open, the close), the car at night, the table. */
const cableMap = (place: string, seed: number): MapDef | null => {
  if (place === 'cableStation') return { ...createClearingMap(BOX_RADIUS), rocks: boxLayout(seed).rocks };
  if (place === 'cableNight' || place === 'planTable') return createClearingMap(6);
  return null;
};

/** The artificer's chapter 3 scenes: Grada's wall at dawn (the opening's), the warm slab by the river (the campfire). */
const heartMap = (place: string): MapDef | null => (place === 'rajaWall' || place === 'emberSlab' ? createClearingMap(6) : null);

/** The artificer's chapter 4 scenes: Grada's ledge at dawn (the memory's end: its own board), the mine's gallery (the campfire). */
const eyesMap = (place: string): MapDef | null => (place === 'gradaThaw' ? { ...createClearingMap(STRUTS_RADIUS), rocks: strutsLayout(1).rocks } : place === 'mineCamp' ? createClearingMap(6) : null);

/** The artificer's chapter 5 scenes: Tormo's hall (the cinematic, the opening), the workshop's yard (the quarrymen, the campfire, the speech, the close), Grada's top ledge (the epilogue). */
const tormoMap = (place: string): MapDef | null => (place === 'shopYard' ? createClearingMap(8) : place === 'gradaRaja' ? createClearingMap(7) : place === 'tormoDown' ? createClearingMap(KNEES_RADIUS) : null);

/**
 * The barbarian's chapter 1 scenes (the Long Hunt): the corral's own ground (its wall, its gate) by night; the close's
 * ledge end, the drop past its edge (engine/caza.ts CAZA_END_EDGE); a clearing for the council's fire and the cave.
 */
const cazaMap = (place: string): MapDef | null => {
  if (place === 'cazaPens' || place === 'cazaNight') return { ...createClearingMap(PENS_RADIUS), rocks: pensLayout(CAZA_SCENE_SEED).rocks };
  if (place === 'cazaEnd') {
    const m = createClearingMap(7);
    const rocks: { q: number; r: number }[] = [];
    for (let r = -m.radius; r <= m.radius; r++)
      for (let q = -m.radius; q <= m.radius; q++) {
        if (hexLen(q, r) > m.radius) continue;
        const x = hexToWorld(q, r).x;
        if (x > CAZA_END_EDGE + 400 || x < -5300) rocks.push({ q, r });
      }
    return { ...m, rocks };
  }
  return place === 'cazaFire' || place === 'cazaCave' || place === 'cazaFeast' ? createClearingMap(6) : null;
};

/**
 * The barbarian's chapter 2 scenes (the Vertebrae): the arches' own boards (act 1's for the cinematic, the den's for the
 * campfire, of their seed), the rest the scenes' own (engine/vertebras.ts vertSceneMap: the sea but the floor).
 */
const vertMap = (place: string): MapDef | null => {
  if (place === 'vertDen') return { ...createClearingMap(DEN_RADIUS), rocks: denLayout(VERT_SCENE_SEED).rocks };
  return vertSceneMap(place);
};

/** The cleric's chapter 1 scenes (render engine/camino.ts): a lane, the hall, the chapel's terrace, the slope's top. */
const caminoMap = (place: string): MapDef | null => caminoSceneMap(place) ?? pasarelaSceneMap(place) ?? nieveSceneMap(place) ?? ermitaSceneMap(place);

/**
 * The paladin's chapter 2 scenes on the salt flats: the board of this seed (the altar's column on the right, as the
 * scenes' figures stand); the close's with the altar's pool drained.
 */
const SALT_SCENE_SEED = ((): number => {
  for (let k = 1; k < 200; k++) if (saltLayout(k).side > 0) return k;
  return 1;
})();
const SALT_SCENES = new Set(['saltFlats', 'saltNight', 'saltFog']);
const saltMap = (place: string): MapDef | null => (SALT_SCENES.has(place) ? { ...createClearingMap(SALT_RADIUS), rocks: saltLayout(SALT_SCENE_SEED).rocks } : null);

/** The paladin's chapter 3 scenes on the bridge in the rain (its board is the same for every seed but the rain's order). */
const ARCH_SCENES = new Set(['rainBridge', 'rainNight']);
const archMap = (place: string): MapDef | null => (ARCH_SCENES.has(place) ? { ...createClearingMap(ARCH_RADIUS), rocks: archLayout(1).rocks } : null);

/**
 * The paladin's chapter 4 scenes at the monastery of the waterfall (its board is the same for every seed): the memory's
 * morning, dusk at the door, the kitchen at night, the dawn of the test and the low point.
 */
const MON_SCENES = new Set(['gorgePast', 'monastery', 'monasteryNight', 'monasteryDawn']);
const monMap = (place: string): MapDef | null => (MON_SCENES.has(place) ? { ...createClearingMap(MON_RADIUS), rocks: monasteryLayout(1).rocks } : null);

/**
 * The paladin's chapter 5 scenes (their boards the same for every seed): the keep's yard at night (the cinematic and the
 * opening), the cistern under it (the edge, the campfire, the speech, the close), the keep's top (the epilogue).
 */
const SEV_SCENES = new Set(['keepNight', 'keepFar', 'cistern7', 'keepTop']);
const sevMap = (place: string): MapDef | null => {
  if (place === 'keepNight' || place === 'keepFar') return { ...createClearingMap(YARD_RADIUS), rocks: yardLayout().rocks };
  if (place === 'cistern7') return { ...createClearingMap(CIST_RADIUS), rocks: cisternLayout(1).shallow };
  if (place === 'keepTop') return createClearingMap(6);
  return null;
};

/** Who goes on one knee at the speech's end (the knights and Nuño; behind them the salt folk). */
const KNEELERS = new Set(['orderKnight', 'orderKnight2', 'nuno', 'salinera', 'salter', 'salter2']);

/** A scene's board in the cut bridge's gorge: the act's own ground (its drop), by place. */
const chasmMap = (place: string): MapDef | null => {
  if (place === 'rim' || place === 'chasmFar') return { ...createClearingMap(RIM_RADIUS), rocks: rimLayout(CHASM_SCENE_SEED).drop };
  if (place === 'span' || place === 'spanFire') return { ...createClearingMap(SPAN_RADIUS), rocks: spanLayout(CHASM_SCENE_SEED).drop };
  if (place === 'weights') return { ...createClearingMap(WEIGHTS_RADIUS), rocks: weightsLayout().drop };
  return null;
};
import { BattleView, caminoSceneMap, ermitaSceneMap, NAVE_SCENE_SEED, nieveSceneMap, pasarelaSceneMap, boteSceneMap, islaSceneMap, rocaSceneMap, CAZA_END_EDGE, CAZA_SCENE_SEED, Post, simToWorld, vertSceneMap, VERT_SCENE_SEED, WORLD_PER_HEX } from '@somnhold/render';
import { audio } from '../audio/sfx.ts';
import { t } from '../i18n.ts';
import { settings } from '../settings.ts';
import { clear, h } from '../ui/dom.ts';
import { Loop } from './loop.ts';

/**
 * A cinematic made with the game's own engine (docs/SENDAS.md, «Decisiones para construir»): a board built for the
 * scene (its place, its hour and weather), the people standing on it in their poses, a camera that glides from key to
 * key, black bars, fades, and the lines below with the speaker's face and name. A tap (or Space) shows a line whole,
 * then the next; «Saltar» goes to the end. Under the lines the stage can hold questions (the campfire), a decision
 * or a card (what comes next). No videos, nothing per game: the board is the one every fight draws, held still.
 *
 * Cost: one BattleView with a handful of figures and no nightmares moving (no sim steps after the first ones that
 * put them there); the camera is the only thing that moves. Its models bake and its shaders link before the fade
 * opens (docs/RENDIMIENTO.md, «costo de entrada»).
 */

export interface CineLine {
  /** Who says it: their name over the line (empty: the narrator, in italics). */
  name: string;
  text: string;
  /** Their face, drawn once (null: none). */
  face: string | null;
  /** Said, it lights this slot of Ruy's line red (the warrior's chapter 5 speech, 1 to 11). */
  light?: number;
  /** Said, the scene's own moment (the paladin's chapter 5 speech: `kneel`). */
  cue?: string;
}

export interface CineOption {
  label: string;
  desc?: string;
  /** Already asked (the campfire): shown, not tappable. */
  done?: boolean;
  test: string;
  run: () => void;
}

export interface CineSetup {
  cine: SendaCine;
  /** The hero on stage (`hero` among the figures). */
  hero: { pid: string; name: string; cls: HeroClass; level: number; look?: string };
  /** The path's class: its people's miniatures come from its cast. */
  cls: HeroClass;
  /** Over the title: the path and the chapter; the title: the chapter's or the act's. */
  kicker: string;
  title: string;
}

/** A sixth of a turn in the sim's facing (1024 a turn). */
const SIXTH = 1024 / 6;

/** The weather's dressing (an omen's look, cosmetic): the wind drifts the fog until the paths bring their own. */
const WEATHER: Record<SendaWeather, string | undefined> = { clear: undefined, snow: 'frost', wind: 'fog', storm: 'storm', fog: 'fog', rain: 'storm' };

/** What each pose holds: a clip and the moment of it (the fall's first buckle is a knee on the ground). */
const POSE: Record<NonNullable<SendaFigure['pose']>, { clip: string; t?: number } | null> = {
  stand: null,
  walk: { clip: 'walk' },
  fight: { clip: 'attack' },
  kneel: { clip: 'kneel' },
  sit: { clip: 'sit' },
  lie: { clip: 'down', t: 1.6 },
};

const ease = (x: number): number => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

/** How long a line's letters take to come in (ms per letter), and at most. */
const LETTER_MS = 22;
const LETTERS_MAX_MS = 1600;

export class Cinematic {
  readonly el: HTMLElement;
  private battle: BattleState;
  private view: BattleView;
  private camera = new THREE.PerspectiveCamera(40, 1, 0.1, 400);
  private post: Post;
  private loop: Loop;
  private keys: { from: THREE.Vector3; at: THREE.Vector3; ms: number }[] = [];
  private t0 = 0;
  private opened = false;
  private shownAt = 0;
  private walkers: { id: number; enemy: boolean; dx: number; dy: number; from: number; until: number }[] = [];
  private faceCache = new Map<string, string | null>();

  // The page.
  private veil = h('div.cine-veil.on');
  private titleEl: HTMLElement;
  private lineBox = h('div.cine-line', { 'aria-live': 'polite' });
  private faceEl = h('span.cine-face');
  private nameEl = h('b.cine-name');
  private textEl = h('p.cine-text');
  private hintEl = h('span.cine-hint', {}, t('cine.tap'));
  private under = h('div.cine-under');
  private skipBtn: HTMLButtonElement;

  // The lines being told.
  private lines: CineLine[] = [];
  private at = -1;
  private typing: { full: string; start: number; ms: number } | null = null;
  private afterLines: (() => void) | null = null;

  constructor(
    private renderer: THREE.WebGLRenderer,
    private setup: CineSetup,
    private faces: (who: string) => string | null,
  ) {
    this.battle = this.make();
    this.view = new BattleView(this.battle.map, this.battle.seed, { quality: settings.quality, me: '' });
    this.place();
    this.view.sync(this.battle);
    const c = setup.cine;
    const weather = c.weather ? WEATHER[c.weather] : undefined;
    const fire = c.fire ? hexToWorld(c.fire.q, c.fire.r) : undefined;
    this.view.setCinema({ ...(c.sky ? { sky: c.sky } : {}), ...(weather ? { weather } : {}), ...(fire ? { fire } : {}), ...(c.flood ? { flood: true } : {}), ...(c.torches ? { torches: true } : {}), ...(c.eye ? { eye: true } : {}), ...(c.broken ? { broken: true } : {}), ...(c.face ? { face: true } : {}), ...(c.breath ? { breath: true } : {}) });
    this.keys = c.cam.map((k) => ({ from: this.point(k.from), at: this.point(k.at), ms: Math.max(400, k.ms) }));
    // Nothing of the board's dressing between the camera and the scene along its way.
    const segs: [THREE.Vector3, THREE.Vector3][] = [];
    this.keys.forEach((k, i) => {
      const next = this.keys[i + 1];
      if (next) segs.push([k.from, next.from]);
      segs.push([k.from, k.from.clone().lerp(k.at, 0.75)]);
    });
    if (!c.scope) this.view.clearWay(segs, 1.3);
    this.post = new Post(renderer, this.view.scene, this.camera, settings.tiltShift);
    this.titleEl = h('div.cine-title', {}, h('span.cine-kicker', {}, setup.kicker), h('h2', {}, setup.title));
    this.skipBtn = h('button.cine-skip', { type: 'button', 'data-test': 'cine-skip', onclick: (e: Event) => (e.stopPropagation(), this.skip()) }, t('cine.skip')) as HTMLButtonElement;
    this.lineBox.append(h('div.cine-who', {}, this.faceEl, this.nameEl), this.textEl, this.hintEl);
    this.el = h(
      `div.game.cine${setup.cine.scope ? '.scope' : ''}${setup.cine.memory ? '.memory' : ''}`,
      { 'data-test': 'cine', onpointerup: ((e: Event) => this.tap(e)) as EventListener },
      renderer.domElement,
      h('div.cine-bar.top'),
      h('div.cine-bar.bottom'),
      this.titleEl,
      h('div.cine-foot', {}, this.lineBox, this.under),
      this.skipBtn,
      this.veil,
    );
    this.loop = new Loop(
      () => this.tick(),
      (dt) => this.frame(dt),
    );
    window.addEventListener('resize', this.resize);
    window.addEventListener('keydown', this.key);
    this.resize();
  }

  /** The scene's board: a clearing for its place, the hero and the path's people as figures, nightmares standing. */
  private make(): BattleState {
    const c = this.setup.cine;
    const seed = 7300 + [...c.place].reduce((a, ch) => a + ch.charCodeAt(0), 0);
    // The ledge (the warrior's chapter 1): the wedges' own ground, after the fight (its shields in, the cornice down).
    const ledge = c.place === 'ledge' ? wedgeLayout(LEDGE_SCENE_SEED) : null;
    const chasm = chasmMap(c.place);
    const fort = fortMap(c.place);
    const slide = slideMap(c.place);
    const pass = passMap(c.place);
    const altars = altarMap(c.place, !!c.lit);
    const flats = saltMap(c.place);
    const bridgeAltar = archMap(c.place);
    const monastery = monMap(c.place);
    const seventh = sevMap(c.place);
    const taller = tallerMap(c.place) ?? cableMap(c.place, seed) ?? heartMap(c.place) ?? eyesMap(c.place) ?? tormoMap(c.place) ?? biblioMap(c.place, seed) ?? asterMap(c.place);
    const caza = cazaMap(c.place) ?? vertMap(c.place) ?? boteSceneMap(c.place) ?? islaSceneMap(c.place) ?? rocaSceneMap(c.place);
    const camino = caminoMap(c.place);
    const bosque = bosqueMap(c.place, seed) ?? hornosMap(c.place, seed) ?? otonoMap(c.place, seed) ?? copaMap(c.place, seed) ?? raicesMap(c.place, seed);
    const map: MapDef = ledge ? { ...createClearingMap(WEDGE_RADIUS), rocks: ledge.rocks } : (chasm ?? fort ?? slide ?? pass ?? altars ?? flats ?? bridgeAltar ?? monastery ?? seventh ?? taller ?? caza ?? camino ?? bosque ?? createIncursionMap(seed));
    const figs = c.figures ?? [];
    const hero = this.setup.hero;
    const people = figs.filter((f) => !this.foe(f.who));
    const heroes = people.map((f, i) =>
      f.who === 'hero'
        ? { pid: hero.pid, name: hero.name, cls: hero.cls, level: hero.level, control: 'live' as const, powerBp: 10000, post: i, ...(hero.look ? { look: hero.look } : {}) }
        : { pid: `npc:${f.who}`, name: '', cls: this.setup.cls, level: 10, control: 'live' as const, powerBp: 10000, post: i, face: f.who },
    );
    const foes = figs.filter((f) => this.foe(f.who));
    const b = createBattle({
      mode: 'incursion',
      seed: chasm ? CHASM_SCENE_SEED : fort ? FORT_SCENE_SEED : slide ? SLIDE_SCENE_SEED : pass ? PASS_SCENE_SEED : c.place.startsWith('nave') ? NAVE_SCENE_SEED : seed,
      map,
      // Buildings standing in the place (the bastion's barracks), in stone.
      structures: (c.structures ?? []).map((st) => ({ type: st.type, level: 2 as const, material: 2 as const, q: st.q, r: st.r })),
      heroes,
      keepHp: 99999,
      keepMaxHp: 99999,
      marea: 0,
      wave: 0,
      spawns: foes.map((f, i) => ({ tick: 1 + i, sector: 0, kind: f.who as EnemyKind, tier: 1 as const, elite: false, at: { q: f.q, r: f.r } })),
      objectiveTicks: 99999,
      rules: { place: c.place },
    });
    if (ledge) {
      // The wedges after the fight: the right three shields in, the rock gone, the cornice down on the strip.
      const R = map.radius;
      const id = (hx: { q: number; r: number }): number => cellId(R, hx.q, hx.r);
      const gaps = ledge.gaps.map(id);
      b.rules = {
        ...(b.rules ?? {}),
        senda: {
          goal: { kind: 'wedge', need: 1 },
          npcs: [],
          says: [],
          hit: { every: 12, range: 0, flinch: 0 },
          follow: 0,
          speed: 0,
          own: true,
          wedge: { gaps, strip: ledge.strip.map(id), nook: id(ledge.nook), entry: id(ledge.entry), shields: 3, stand: 24, reach: 1100, fall: 36, need: 8, lose: 6, door: 800, slowBp: 5000 },
        },
      };
      b.plugs = ledge.solution.map((i) => gaps[i] as number).sort((x, y) => x - y);
      b.senda = { npcs: [], work: 0, said: [], half: false, boss: false, nums: {}, wedge: { shut: gaps.map((_, i) => (ledge.solution.includes(i) ? 1 : 0)), hand: 0, at: -1, held: 0, pushed: 0, down: true, count: 0, entered: 0, narrow: [], rev: -1 } };
    }
    if (c.place === 'weights') {
      // The counterweight solved: the pieces on their pans as the answer hangs them, the spans level (they come down
      // as the scene opens, `spans`).
      const L = weightsLayout();
      const W = weightLayout(CHASM_SCENE_SEED);
      const at = (hx: { q: number; r: number }): { x: number; y: number } => hexToWorld(hx.q, hx.r);
      const way = W.ways[0] ?? [];
      b.rules = {
        ...(b.rules ?? {}),
        senda: {
          goal: { kind: 'weight', need: 1 },
          npcs: [],
          says: [],
          hit: { every: 12, range: 0, flinch: 0 },
          follow: 0,
          speed: 0,
          own: true,
          weight: { pans: L.pans.map(at), pile: at(L.pile), posts: [...W.posts], pieces: WEIGHT_PIECES.map((p) => ({ ...p })), pick: 12, put: 12, reach: 1100, carryBp: 7000, haul: 96, restock: 120, hold: 24, serpent: { hp: 1, warn: 36, every: 180, first: 144, grab: 24, up: 60, scare: 2 } },
        },
      };
      b.senda = {
        npcs: [],
        work: 0,
        said: [],
        half: false,
        boss: false,
        nums: {},
        weight: { on: WEIGHT_PIECES.map((_, i) => (way[i] ?? 3) < 3 ? (way[i] as number) : -1), back: WEIGHT_PIECES.map(() => 0), job: -1, to: -1, held: 0, haul: -1, hauled: 0, level: 0, snake: 0, phase: 0, pan: -1, since: 0, blows: 0, took: false },
      };
    }
    if (c.place === 'glassShore') {
      // The shore's canals; the close's with both pools full and their eyes open, a thread of still water between them.
      const L = canalLayout(SHORE_SCENE_SEED, c.lit ? 'ebb' : 'small');
      const canal = canalRules(L, map.radius, { every: 24, hit: 1, reach: 1150, guard: 2400, speed: 38 }, 1);
      b.rules = { ...(b.rules ?? {}), senda: { goal: { kind: 'pool', need: 1 }, npcs: [], says: [], hit: { every: 12, range: 0, flinch: 0 }, follow: 0, speed: 0, own: true, canal } };
      const st = canalFresh(canal);
      if (c.lit) {
        st.level = canal.pools.map(() => canal.fill);
        st.lit = 0;
        st.pos = [...L.answer];
      }
      b.senda = { npcs: [], work: 0, said: [], half: false, boss: false, nums: {}, canal: st };
    }
    if (SALT_SCENES.has(c.place)) {
      // The salt flats as the gates found them; the close's with the altar's pool drained and its eye open.
      const L = saltLayout(SALT_SCENE_SEED);
      const salt = saltRules(L, map.radius, 'sluice', { every: 24, hit: 1, reach: 1150, guard: 2400, speed: 38 }, SALT_SCENE_SEED, 1, 1);
      b.rules = { ...(b.rules ?? {}), senda: { goal: { kind: 'drain', need: 1 }, npcs: [], says: [], hit: { every: 12, range: 0, flinch: 0 }, follow: 0, speed: 0, own: true, salt } };
      const st = saltFresh(salt);
      if (c.lit) {
        // The way the gates were turned, run: the pools stepped down, the altar's dry.
        for (const g of L.solutions[0] ?? []) {
          const G = L.gates[g];
          if (!G) continue;
          const [na, nb] = sluiceFlow(G.down, st.level[G.a] as number, st.level[G.b] as number, salt.cap);
          st.level[G.a] = na;
          st.level[G.b] = nb;
        }
        st.dried = 0;
      }
      b.senda = { npcs: [], work: 0, said: [], half: false, boss: false, nums: {}, salt: st };
    }
    if (ARCH_SCENES.has(c.place)) {
      // The bridge: its cistern full, the altar burning at night; the close's with the rain stopped and the moon in it.
      const arch = archRules(archLayout(1), c.place === 'rainNight' ? 'vigil' : 'gutters', { every: 24, hit: 1, reach: 1150, guard: 2400, speed: 38 }, 1, 1);
      arch.bands.at = [];
      arch.bands.side = [];
      if (arch.mammoth) arch.mammoth.at = [1_000_000_000, 1_000_000_000];
      b.rules = { ...(b.rules ?? {}), senda: { goal: { kind: 'cistern', need: 1 }, npcs: [], says: [], hit: { every: 12, range: 0, flinch: 0 }, follow: 0, speed: 0, own: true, arch } };
      const st = archFresh(arch);
      st.level = c.place === 'rainNight' ? 8 : 3;
      if (c.lit) st.felled = 2;
      b.senda = { npcs: [], work: 0, said: [], half: false, boss: false, nums: {}, arch: st };
    }
    if (MON_SCENES.has(c.place)) {
      // The monastery: the fifth altar burning (the memory, dusk, the night); the dawn's with the cistern drained and the
      // sixth altar lit (the test won). Nothing comes up the gorge, out of the cells or over the wall.
      const kind = c.place === 'gorgePast' ? 'porch' : c.place === 'monasteryDawn' ? 'cloister' : 'cells';
      const guard = { every: 24, hit: 1, reach: 1150, guard: 2400, speed: 38 };
      const mon = monasteryRules(monasteryLayout(1), kind, guard, guard, 1);
      if (mon.porch) mon.porch.bands.at = [];
      if (mon.cells) mon.cells.first = 1_000_000_000;
      if (mon.cloister) {
        mon.cloister.siege.at = [];
        mon.cloister.boss.late = 1_000_000_000;
        mon.cloister.fall = 1_000_000_000;
      }
      b.rules = { ...(b.rules ?? {}), senda: { goal: { kind: 'cell', need: 1 }, npcs: [], says: [], hit: { every: 12, range: 0, flinch: 0 }, follow: 0, speed: 0, own: true, monastery: mon } };
      const st = monasteryFresh(mon);
      if (mon.cloister) {
        st.level = 1;
        st.lit = true;
        st.bossId = -1;
      }
      b.senda = { npcs: [], work: 0, said: [], half: false, boss: false, nums: {}, monastery: st };
    }
    if (SEV_SCENES.has(c.place) && c.place !== 'keepTop') {
      // The seventh altar: the yard with its rubble still over the stair (nothing comes up the drains); the cistern brimming
      // by the campfire, and after the test (the speech, the close) its water a ring at three marks, the island bare.
      const guard = { every: 24, hit: 1, reach: 1150, guard: 2400, speed: 38 };
      const full = !!c.fire;
      const kind = c.place === 'cistern7' ? (full ? 'well' : 'fled') : 'rubble';
      const sev = seventhRules(kind, yardLayout(), cisternLayout(1), guard, 1, { hp: 1, together: true, ring: 3 });
      if (sev.rubble) {
        sev.rubble.first = 1_000_000_000;
        sev.rubble.singer = [];
      }
      sev.shield.first = 1_000_000_000;
      if (sev.fled) sev.fled.shield.first = 1_000_000_000;
      b.rules = { ...(b.rules ?? {}), senda: { goal: { kind: kind === 'rubble' ? 'rubble' : kind === 'well' ? 'ring' : 'fled', need: 1 }, npcs: [], says: [], hit: { every: 12, range: 0, flinch: 0 }, follow: 0, speed: 0, own: true, seventh: sev } };
      const st = seventhFresh(sev);
      // (Never her: the board's fight never starts, and she has her id already.)
      st.boss = -1;
      b.senda = { npcs: [], work: 0, said: [], half: false, boss: false, nums: {}, seventh: st };
    }
    // Only to bring the nightmares on: then the board stands still and the stage moves it by hand.
    for (let i = 0; i < foes.length + 2; i++) stepBattle(b);
    return b;
  }

  /** Each figure where the scene puts it, turned and posed; the walkers keep walking the way they face. */
  private place(): void {
    const figs = this.setup.cine.figures ?? [];
    const people = figs.filter((f) => !this.foe(f.who));
    const foes = figs.filter((f) => this.foe(f.who));
    this.view.posed.clear();
    this.walkers = [];
    people.forEach((f) => {
      // The board keeps its heroes by player id: each figure finds its own.
      const pid = f.who === 'hero' ? this.setup.hero.pid : `npc:${f.who}`;
      const hero = this.battle.heroes.find((x) => x.pid === pid) as Hero | undefined;
      if (!hero) return;
      const w = hexToWorld(f.q, f.r);
      hero.x = w.x;
      hero.y = w.y;
      hero.facing = Math.round((f.facing ?? 0) * SIXTH) % 1024;
      hero.anim = 'idle';
      hero.downTicks = 0;
      const pose = POSE[f.pose ?? 'stand'];
      if (pose) this.view.posed.set(hero.id, pose);
      if (f.pose === 'walk') this.walkers.push({ id: hero.id, enemy: false, ...this.dir(hero.facing, f.speed), from: f.delay ?? 0, until: f.walkMs ? (f.delay ?? 0) + f.walkMs : Infinity });
      // A walker that waits for its moment stands still until then.
      if (f.pose === 'walk' && f.delay) this.view.posed.delete(hero.id);
    });
    // The nightmares came on in order: the first one standing for each figure.
    const alive = this.battle.enemies.filter((e) => e.hp > 0);
    foes.forEach((f, i) => {
      const e = alive[i];
      if (!e) return;
      const w = hexToWorld(f.q, f.r);
      e.x = w.x;
      e.y = w.y;
      e.facing = Math.round((f.facing ?? 0) * SIXTH) % 1024;
      e.anim = f.pose === 'walk' ? 'walk' : f.pose === 'fight' ? 'attack' : 'idle';
      if (f.pose === 'walk') this.walkers.push({ id: e.id, enemy: true, ...this.dir(e.facing, f.speed), from: f.delay ?? 0, until: f.walkMs ? (f.delay ?? 0) + f.walkMs : Infinity });
    });
  }

  /** The figures who kneel at the speech's end go down on one knee, the board's moment with them. */
  private kneel(): void {
    const pose = POSE.kneel;
    for (const f of this.setup.cine.figures ?? []) {
      if (!KNEELERS.has(f.who) || !pose) continue;
      const hero = this.battle.heroes.find((x) => x.pid === `npc:${f.who}`);
      if (hero) this.view.posed.set(hero.id, pose);
    }
    this.view.seventhCue('kneel');
  }

  /** A nightmare on stage (one of the path's people by a nightmare's name, Ser Edda as she was, is a person). */
  private foe(who: string): boolean {
    return who in NIGHTMARES && !sendaForClass(this.setup.cls)?.cast?.[who];
  }

  private dir(facing: number, speed = 26): { dx: number; dy: number } {
    const a = (facing / 1024) * Math.PI * 2;
    return { dx: Math.round(Math.cos(a) * speed), dy: Math.round(Math.sin(a) * speed) };
  }

  /** A camera key's point: a hex and a height in hexes, over the ground there. */
  private point([q, r, hx]: [number, number, number]): THREE.Vector3 {
    const w = hexToWorld(q, r);
    return simToWorld(w.x, w.y, new THREE.Vector3(), hx * WORLD_PER_HEX + 0.2);
  }

  /** The stage holds the board still: its clock moves for the drawing (breathing, fire), never the fight. */
  private tick(): void {
    this.battle.tick++;
    // The walkers walk from their moment (counted from the curtain lifting) for as long as they're told.
    const t = this.opened ? performance.now() - this.t0 : -1;
    for (const w of this.walkers) {
      const o = w.enemy ? this.battle.enemies.find((e) => e.id === w.id) : this.battle.heroes.find((x) => x.id === w.id);
      if (!o) continue;
      const on = t >= w.from && t < w.until;
      if (!w.enemy) {
        if (on) this.view.posed.set(o.id, { clip: 'walk' });
        else this.view.posed.delete(o.id);
      }
      if (!on) continue;
      o.x += w.dx;
      o.y += w.dy;
    }
    this.battle._events = [];
    this.view.sync(this.battle);
  }

  private frame(dt: number): void {
    void dt;
    if (!this.opened) {
      // Baked and linked before the curtain lifts (never more than two seconds).
      const ready = !this.view.bakeAhead(this.battle, 30) && this.view.readyAhead;
      if (!ready && performance.now() - this.shownAt < 2000) {
        this.view.frame(this.camera);
        return;
      }
      this.opened = true;
      this.t0 = performance.now();
      // The counterweight's spans come down together as the curtain lifts (the warrior's chapter 2 close).
      if (this.setup.cine.spans) this.view.lowerSpans();
      // The fort's roof tips over the far yard a moment in (Fermín's «¡Ahora!», the chapter 3 memory's end).
      if (this.setup.cine.tip) window.setTimeout(() => this.view.tipRoof(), 1600);
      // The pass's close: Harl's shield back in its gap, the whole line red.
      if (this.setup.cine.whole) window.setTimeout(() => this.view.passLight(-1), 1200);
      this.el.classList.add('open');
      this.veil.classList.remove('on');
      // The title holds a moment alone; the first line comes in as it goes.
      window.setTimeout(() => this.titleEl.classList.add('gone'), 2900);
      window.setTimeout(() => this.afterLines && this.at < 0 && !this.holding && this.next(), 3500);
    }
    // A cinematic without words: on at the end of its camera path.
    if (this.holding && performance.now() - this.t0 >= this.camMs()) {
      this.holding = false;
      this.next();
    }
    this.aim(performance.now() - this.t0);
    this.view.interpolate(this.battle, this.loop.alpha);
    this.view.frame(this.camera);
    this.post.render();
    this.type();
  }

  /** The camera along its keys (eased between each), then a slow drift in past the last. */
  private aim(ms: number): void {
    const k = this.keys;
    if (!k.length) return;
    let at = ms;
    let i = 0;
    while (i < k.length - 1 && at > (k[i] as (typeof k)[number]).ms) {
      at -= (k[i] as (typeof k)[number]).ms;
      i++;
    }
    const a = k[i] as (typeof k)[number];
    const b = k[i + 1];
    const from = new THREE.Vector3();
    const look = new THREE.Vector3();
    if (b) {
      const u = ease(at / a.ms);
      from.lerpVectors(a.from, b.from, u);
      look.lerpVectors(a.at, b.at, u);
    } else {
      // Held on the last key, the camera breathes in a little (a few per cent a minute).
      const drift = Math.min(0.12, (at / 60000) * 0.12);
      from.copy(a.from).lerp(a.at, drift);
      look.copy(a.at);
    }
    // A tall screen sees less across: back off along the line of sight so the people still fit.
    const tall = this.camera.aspect < 1 ? 1 + (1 / this.camera.aspect - 1) * 0.3 : 1;
    // (Never lower than the key put it: a camera looking up at something far, backed off, would sink into the ground.)
    if (tall > 1) {
      const y = from.y;
      from.sub(look).multiplyScalar(tall).add(look);
      if (from.y < y) from.y = y;
    }
    this.camera.position.copy(from);
    this.camera.lookAt(look);
  }

  /* ---------- the lines ---------- */

  /**
   * Tells these lines one by one; `done` once the last has been read. `hold`: with no lines, the camera's whole path
   * plays first (a cinematic without words), a tap or «Saltar» cutting it short.
   */
  tell(lines: CineLine[], done: () => void, hold = false): void {
    this.lines = lines;
    this.at = -1;
    this.afterLines = done;
    this.holding = hold && !lines.length;
    clear(this.under);
    this.under.classList.remove('on');
    if (this.opened && !this.holding) this.next();
  }

  /** A cinematic without words is playing its camera path (it goes on by itself at the end). */
  private holding = false;

  /** The camera path's length (ms). */
  private camMs(): number {
    return this.keys.reduce((a, k) => a + k.ms, 0);
  }

  private next(): void {
    if (!this.lines.length && this.afterLines) {
      const f = this.afterLines;
      this.afterLines = null;
      return f();
    }
    if (this.at >= this.lines.length - 1) {
      this.lineBox.classList.remove('on');
      const f = this.afterLines;
      this.afterLines = null;
      f?.();
      return;
    }
    this.at++;
    const l = this.lines[this.at] as CineLine;
    // The speech's names: each one said lights its shield (the line's slots, 1 to 11).
    if (l.light) this.view.passLight(l.light);
    // The seventh altar's speech: the knights and Nuño on one knee, the salt folk behind them; the water trembles.
    if (l.cue === 'kneel') this.kneel();
    // The druid's chapter 4: the bough cut, Hob going down with it through the cloud; the cure, the snow let go.
    else if (l.cue) {
      this.view.copaCue(l.cue);
      if (l.cue === 'fall') {
        const hob = this.battle.heroes.find((x) => x.pid === 'npc:hob');
        if (hob) hob.x += 90000;
      }
    }
    clear(this.faceEl);
    this.faceEl.classList.toggle('none', !l.face);
    if (l.face) this.faceEl.append(h('img', { src: l.face, alt: '' }));
    this.nameEl.textContent = l.name;
    this.lineBox.classList.toggle('narrator', !l.name);
    this.textEl.textContent = '';
    this.typing = { full: l.text, start: performance.now(), ms: Math.min(LETTERS_MAX_MS, l.text.length * LETTER_MS) };
    this.lineBox.classList.remove('on');
    // A beat for the old line to fade before the new one comes in.
    void this.lineBox.offsetWidth;
    this.lineBox.classList.add('on');
    this.hintEl.classList.remove('show');
  }

  private type(): void {
    const ty = this.typing;
    if (!ty) return;
    const k = Math.min(1, (performance.now() - ty.start) / Math.max(1, ty.ms));
    const n = Math.round(ty.full.length * k);
    const shown = ty.full.slice(0, n);
    if (this.textEl.textContent !== shown) this.textEl.textContent = shown;
    if (k >= 1) {
      this.typing = null;
      this.hintEl.classList.add('show');
    }
  }

  private tap(e: Event): void {
    if ((e.target as HTMLElement).closest('button')) return;
    this.advance();
  }

  private advance(): void {
    if (!this.opened || !this.afterLines) return;
    if (this.holding) {
      this.holding = false;
      this.next();
      return;
    }
    if (this.typing) {
      this.textEl.textContent = this.typing.full;
      this.typing = null;
      this.hintEl.classList.add('show');
      return;
    }
    audio.play('click', 0.35);
    this.next();
  }

  private key = (e: KeyboardEvent): void => {
    if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowRight') {
      if (this.afterLines) {
        e.preventDefault();
        this.advance();
      }
    } else if (e.key === 'Escape') this.skip();
  };

  /** Straight to the end of what is being told (the lines' end, where the choices wait). */
  private skip(): void {
    if (!this.afterLines) return;
    this.holding = false;
    this.titleEl.classList.add('gone');
    this.typing = null;
    this.at = this.lines.length - 1;
    this.lineBox.classList.remove('on');
    const f = this.afterLines;
    this.afterLines = null;
    f();
  }

  /* ---------- under the lines: questions, a decision, a card ---------- */

  /** Options below (the campfire's questions, the decision), with a heading and a note. */
  ask(heading: string, note: string, options: CineOption[], extra: HTMLElement[] = []): void {
    this.lineBox.classList.remove('on');
    clear(this.under);
    this.under.append(
      h('div.cine-ask-head', {}, h('b', {}, heading), note ? h('span', {}, note) : ''),
      h(
        'div.cine-options',
        {},
        ...options.map((o) =>
          o.done
            ? h('div.cine-option.done', { 'data-test': o.test }, h('b', {}, o.label), h('span.cine-done', {}, t('cine.asked')))
            : h(
                'button.cine-option',
                { type: 'button', 'data-test': o.test, onclick: (e: Event) => (e.stopPropagation(), audio.play('click'), o.run()) },
                h('b', {}, o.label),
                o.desc ? h('span', {}, o.desc) : '',
              ),
        ),
      ),
      ...extra,
    );
    this.under.classList.add('on');
    this.skipBtn.hidden = true;
  }

  /** A card below the lines: what comes next, a chapter closed (its buttons). */
  card(content: HTMLElement): void {
    this.lineBox.classList.remove('on');
    clear(this.under);
    this.under.append(content);
    this.under.classList.add('on');
    this.skipBtn.hidden = true;
  }

  /** Back to the lines (after a question answered). */
  clearUnder(): void {
    clear(this.under);
    this.under.classList.remove('on');
    this.skipBtn.hidden = false;
  }

  /** Fades to black, then `then`. */
  close(then: () => void): void {
    this.veil.classList.add('on');
    window.setTimeout(then, 450);
  }

  /** A face once per speaker (drawn on the stage's renderer before the scene opens). */
  face(who: string): string | null {
    if (!this.faceCache.has(who)) this.faceCache.set(who, this.faces(who));
    return this.faceCache.get(who) ?? null;
  }

  private resize = (): void => {
    const pr = Math.min(window.devicePixelRatio || 1, 1.5);
    const w = window.innerWidth;
    const ht = window.innerHeight;
    this.renderer.setPixelRatio(pr);
    this.renderer.setSize(w, ht, false);
    this.camera.aspect = w / ht;
    // About sixty degrees across on a wide screen; a tall one gets a wider look up and down, not a narrower one across.
    const hfov = 58;
    const v = (2 * Math.atan(Math.tan(((hfov / 2) * Math.PI) / 180) / this.camera.aspect) * 180) / Math.PI;
    this.camera.fov = Math.max(34, Math.min(66, v));
    // Through a spyglass: a long lens, a narrow look.
    // On a tall screen the lens is as wide as the screen: the look across it, not up and down, sets the lens.
    if (this.setup.cine.scope) this.camera.fov = w < ht ? Math.max(16, Math.min(40, v * 0.42)) : Math.max(16, Math.min(26, v * 0.5));
    // On a tall screen the lines take the lower third: the scene is drawn higher, centred on what is left above.
    // (The spyglass has no lines under it: its round view stays centred, where the CSS draws the lens.)
    if (w < ht && !this.setup.cine.scope) this.camera.setViewOffset(w, ht, 0, Math.round(ht * 0.15), w, ht);
    else this.camera.clearViewOffset();
    this.camera.updateProjectionMatrix();
    this.post.setSize(w, ht, pr);
  };

  start(): void {
    this.shownAt = performance.now();
    this.loop.start();
    void this.post.compile().catch(() => undefined);
    (window as unknown as { __somnhold?: unknown }).__somnhold = { cine: this, state: this.battle };
  }

  destroy(): void {
    this.loop.stop();
    window.removeEventListener('resize', this.resize);
    window.removeEventListener('keydown', this.key);
    this.view.dispose();
  }
}
